import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Send, FileCheck, Compass } from 'lucide-react';

/**
 * AfterSubmitSteps — small "what happens next" timeline.
 *
 * Used above the form on /contatti, /brochure, /fse, /bookacall.
 * Three sober steps, premium card, no aggressive promises.
 *
 * Variants change the middle step copy slightly:
 *  - "form"      → ricevi proposta entro 24h
 *  - "brochure"  → ricevi il PDF e una sintesi
 *  - "fse"       → ricevi la guida FSE 2.0
 *  - "demo"      → ricevi il calendario della demo
 */
const COPY = {
  it: {
    eyebrow: 'Cosa succede dopo',
    steps: {
      form: [
        { icon: Send,      title: 'Lasci i dati essenziali',     body: 'Niente moduli infiniti: solo l\'essenziale per capire il tuo studio.' },
        { icon: FileCheck, title: 'Ricevi una proposta concreta', body: 'Entro 24h ti scriviamo con una proposta chiara — nessuna call obbligatoria.' },
        { icon: Compass,   title: 'Valuti con calma',             body: 'Decidi se vuoi una demo guidata o se preferisci tornare con domande precise.' },
      ],
      brochure: [
        { icon: Send,      title: 'Compili il form (2 minuti)',   body: 'Bastano i dati essenziali per ricevere materiale rilevante per il tuo studio.' },
        { icon: FileCheck, title: 'Ricevi il PDF subito',          body: 'La brochure arriva via email + una mini-sintesi dei punti chiave.' },
        { icon: Compass,   title: 'Valuti senza impegno',          body: 'Decidi tu se richiedere una demo guidata o tornare a chiederci dettagli.' },
      ],
      fse: [
        { icon: Send,      title: 'Compili il form (2 minuti)',   body: 'Ci dici qualcosa sul tuo studio: ci aiuta a calibrare la guida.' },
        { icon: FileCheck, title: 'Ricevi la guida FSE 2.0',       body: 'Riceverai subito il PDF con i passaggi pratici per un percorso FSE 2.0 ordinato.' },
        { icon: Compass,   title: 'Valuti con calma',              body: 'Niente call commerciali a sorpresa. Se vuoi, ci scrivi tu su WhatsApp.' },
      ],
      demo: [
        { icon: Send,      title: 'Scegli giorno e orario',       body: 'Selezioni uno slot di 30 minuti che ti torna comodo. Confermiamo via email.' },
        { icon: FileCheck, title: 'Demo guidata · 30 minuti',      body: 'Vediamo il tuo flusso reale. Niente slide marketing, solo i tuoi processi.' },
        { icon: Compass,   title: 'Valuti senza impegno',          body: 'A fine demo ti diciamo onestamente se Prime Vision fa al caso tuo.' },
      ],
    },
  },
  en: {
    eyebrow: 'What happens next',
    steps: {
      form: [
        { icon: Send,      title: 'Leave the essentials',         body: 'No endless forms: just what we need to understand your practice.' },
        { icon: FileCheck, title: 'Get a concrete proposal',      body: 'Within 24h we email you a clear proposal — no mandatory call.' },
        { icon: Compass,   title: 'Take your time',                body: 'You decide whether to book a guided demo or come back with specific questions.' },
      ],
      brochure: [
        { icon: Send,      title: 'Fill the form (2 minutes)',     body: 'Just the essentials so we can send relevant material for your practice.' },
        { icon: FileCheck, title: 'Get the PDF instantly',          body: 'The brochure arrives by email with a short summary of the key points.' },
        { icon: Compass,   title: 'Decide without commitment',      body: 'You choose whether to book a guided demo or come back for more details.' },
      ],
      fse: [
        { icon: Send,      title: 'Fill the form (2 minutes)',     body: 'A bit about your practice helps us calibrate the guide.' },
        { icon: FileCheck, title: 'Get the FSE 2.0 guide',         body: 'You\'ll instantly receive the PDF with practical steps for an ordered FSE 2.0 flow.' },
        { icon: Compass,   title: 'Take your time',                 body: 'No surprise sales calls. If you want, you reach out to us on WhatsApp.' },
      ],
      demo: [
        { icon: Send,      title: 'Pick a day and time',          body: 'Choose a 30-minute slot that suits you. We confirm by email.' },
        { icon: FileCheck, title: '30-minute guided demo',         body: 'We look at your real workflow. No marketing slides, just your processes.' },
        { icon: Compass,   title: 'Decide without commitment',     body: 'At the end of the demo we tell you honestly if Prime Vision is right for you.' },
      ],
    },
  },
};

/**
 * @param {object} props
 * @param {'form'|'brochure'|'fse'|'demo'} [props.variant]  → selects the 3-step copy
 * @param {string} [props.testId]                            → root data-testid
 */
export const AfterSubmitSteps = ({ variant = 'form', testId = 'pv-after-submit-steps' }) => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;
  const steps = c.steps[variant] || c.steps.form;

  return (
    <section data-testid={testId} className="relative bg-white py-12 md:py-16 border-t border-slate-100">
      <div className="pv-container">
        <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {c.eyebrow}
        </span>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`${testId}-step-${i + 1}`}
                className="relative bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-pv-ice border border-pv-teal/20">
                    <Icon className="w-4 h-4 text-pv-teal" />
                  </span>
                  <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase font-semibold text-slate-400">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="mt-4 font-heading font-bold text-[15.5px] md:text-[16.5px] text-pv-ink leading-tight">
                  {s.title}
                </div>
                <p className="mt-2 text-[13.5px] md:text-[14px] text-slate-600 leading-relaxed">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AfterSubmitSteps;
